import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout, { adminFetch, AdminCard, Badge, useAdmin } from '../AdminLayout';

// ── Constantes ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 20;

// Couleurs de statut
const STATUT_COLORS = {
  actif:   '#3D8B5E',
  inactif: '#C0392B',
};

const URL_STATUS_COLORS = {
  ok:    '#3D8B5E',
  error: '#C0392B',
  null:  '#8A8A8A',
};

// ── Composant principal ────────────────────────────────────────────────────────
export default function AidesListPage() {
  return (
    <AdminLayout title="Catalogue des aides">
      <AidesListContent />
    </AdminLayout>
  );
}

// ── Contenu ────────────────────────────────────────────────────────────────────
function AidesListContent() {
  const { dark, border } = useAdmin();
  const textColor  = dark ? '#e0e0e0' : '#2D2D2D';
  const mutedColor = '#8A8A8A';

  const [aides, setAides]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [cursorStack, setCursorStack] = useState([]); // historique pour page précédente
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimeout = useRef(null);

  // ── Chargement ──────────────────────────────────────────────────────────────
  const loadPage = useCallback(async (cursor = null, searchTerm = '') => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE });
      if (cursor)     params.set('cursor', cursor);
      if (searchTerm) params.set('search', searchTerm);

      const res = await adminFetch(`/api/admin/aides/catalogue?${params}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Erreur serveur (${res.status})`);
      }
      const data = await res.json();
      setAides(data.items || []);
      setNextCursor(data.nextCursor || null);
      setTotal(data.total ?? 0);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des aides.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Chargement initial
  useEffect(() => {
    loadPage(null, '');
  }, [loadPage]);

  // ── Recherche avec debounce ──────────────────────────────────────────────────
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearch(val);
      setCursorStack([]);
      loadPage(null, val);
    }, 350);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearch('');
    setCursorStack([]);
    loadPage(null, '');
  };

  // ── Pagination ───────────────────────────────────────────────────────────────
  const handleNext = () => {
    if (!nextCursor) return;
    // Sauvegarder le premier id de la page actuelle pour pouvoir revenir
    setCursorStack(prev => [...prev, aides[0]?.id || null]);
    loadPage(nextCursor, search);
  };

  const handlePrev = () => {
    if (cursorStack.length === 0) return;
    const prevCursor = cursorStack[cursorStack.length - 2] || null;
    setCursorStack(prev => prev.slice(0, -1));
    loadPage(prevCursor, search);
  };

  const isFirstPage = cursorStack.length === 0;

  // ── Formatage ────────────────────────────────────────────────────────────────
  const fmtDate = (ts) => ts ? new Date(ts).toLocaleDateString('fr-FR') : 'Jamais';

  // ── Rendu ─────────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* En-tête page */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        flexWrap: 'wrap', gap: 16, marginBottom: 24,
      }}>
        <div>
          <p style={{ color: mutedColor, fontSize: 14, margin: '4px 0 0' }}>
            {loading ? 'Chargement…' : `${total.toLocaleString('fr-FR')} aide${total !== 1 ? 's' : ''} dans la base`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Link
            to="/admin/aides"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '10px 18px', borderRadius: 10,
              border: `1px solid ${border}`,
              background: 'transparent', color: dark ? '#e0e0e0' : '#2D2D2D',
              fontSize: 14, fontWeight: 500, textDecoration: 'none',
            }}
          >
            ← Vue statistiques
          </Link>
          <Link
            to="/admin/aides/catalogue/new"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '10px 18px', borderRadius: 10,
              border: 'none',
              background: '#D4724A', color: '#fff',
              fontSize: 14, fontWeight: 600, textDecoration: 'none',
            }}
          >
            + Nouvelle aide
          </Link>
        </div>
      </div>

      {/* Barre de recherche */}
      <AdminCard style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label
            htmlFor="search-aides"
            style={{ fontSize: 14, color: mutedColor, flexShrink: 0, minWidth: 100 }}
          >
            Rechercher :
          </label>
          <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
            <input
              id="search-aides"
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Nom, organisme ou identifiant…"
              style={{
                width: '100%', padding: '10px 36px 10px 14px',
                borderRadius: 8, border: `1px solid ${border}`,
                background: dark ? '#0f0f23' : '#F5F3F0',
                color: textColor, fontSize: 14,
                boxSizing: 'border-box',
                outline: 'none',
              }}
              autoComplete="off"
            />
            {searchInput && (
              <button
                onClick={handleSearchClear}
                aria-label="Effacer la recherche"
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: mutedColor, fontSize: 18, lineHeight: 1, padding: 2,
                }}
              >
                ×
              </button>
            )}
          </div>
          {search && (
            <span style={{ fontSize: 13, color: '#D4724A', fontWeight: 500 }}>
              {total} résultat{total !== 1 ? 's' : ''} pour « {search} »
            </span>
          )}
        </div>
      </AdminCard>

      {/* Message d'erreur */}
      {error && (
        <div
          role="alert"
          style={{
            background: '#FEE2E2', border: '1px solid rgba(192,57,43,0.4)',
            borderRadius: 12, padding: '14px 20px', marginBottom: 20,
            color: '#991B1B', fontSize: 14, display: 'flex', alignItems: 'center', gap: 10,
          }}
        >
          <span style={{ fontSize: 18 }}>⚠️</span>
          {error}
        </div>
      )}

      {/* État de chargement */}
      {loading && !error && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              style={{
                height: 52, borderRadius: 10,
                background: dark ? '#1e1e3a' : '#F5F3F0',
                opacity: 0.5 + (i * 0.1),
              }}
            />
          ))}
        </div>
      )}

      {/* Tableau */}
      {!loading && !error && (
        <AdminCard style={{ overflowX: 'auto', padding: 0 }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse', fontSize: 14,
          }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${border}` }}>
                {[
                  { label: 'ID',              width: '10%' },
                  { label: 'Nom de l\'aide',  width: '30%' },
                  { label: 'Organisme',       width: '14%' },
                  { label: 'Statut',          width: '9%'  },
                  { label: 'URL',             width: '7%'  },
                  { label: 'Priorité',        width: '7%'  },
                  { label: 'Dernière vérif.', width: '11%' },
                  { label: 'Actions',         width: '12%' },
                ].map(col => (
                  <th
                    key={col.label}
                    style={{
                      textAlign: 'left', padding: '12px 16px',
                      color: mutedColor, fontWeight: 600, fontSize: 12,
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                      width: col.width,
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {aides.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    style={{ padding: '40px 16px', textAlign: 'center', color: mutedColor, fontSize: 14 }}
                  >
                    {search
                      ? `Aucune aide ne correspond à « ${search} ».`
                      : 'Aucune aide dans la base.'}
                  </td>
                </tr>
              ) : (
                aides.map((aide, idx) => (
                  <tr
                    key={aide.id}
                    style={{
                      borderBottom: `1px solid ${border}`,
                      background: idx % 2 === 0
                        ? 'transparent'
                        : (dark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)'),
                    }}
                  >
                    {/* ID */}
                    <td style={{ padding: '12px 16px' }}>
                      <code style={{
                        fontFamily: 'monospace', fontSize: 12,
                        background: dark ? '#0f0f23' : '#F0EDE8',
                        padding: '2px 6px', borderRadius: 4,
                        color: dark ? '#a8c9b5' : '#3F6B51',
                      }}>
                        {aide.id}
                      </code>
                    </td>

                    {/* Nom */}
                    <td style={{ padding: '12px 16px', color: textColor, fontWeight: 500 }}>
                      {aide.nom}
                    </td>

                    {/* Organisme */}
                    <td style={{ padding: '12px 16px', color: mutedColor, fontSize: 13 }}>
                      {aide.organisme}
                    </td>

                    {/* Statut actif/inactif */}
                    <td style={{ padding: '12px 16px' }}>
                      <Badge color={STATUT_COLORS[aide.statut] || mutedColor}>
                        {aide.statut}
                      </Badge>
                    </td>

                    {/* Statut URL */}
                    <td style={{ padding: '12px 16px' }}>
                      {aide.url_status ? (
                        <Badge color={URL_STATUS_COLORS[aide.url_status] || mutedColor}>
                          {aide.url_status}
                        </Badge>
                      ) : (
                        <span style={{ color: mutedColor, fontSize: 13 }}>—</span>
                      )}
                    </td>

                    {/* Priorité */}
                    <td style={{ padding: '12px 16px', color: textColor, textAlign: 'center' }}>
                      {aide.priorite}
                    </td>

                    {/* Dernière vérification */}
                    <td style={{ padding: '12px 16px', color: mutedColor, fontSize: 13 }}>
                      {fmtDate(aide.derniere_verification)}
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Link
                          to={`/admin/aides/catalogue/${aide.id}`}
                          title="Modifier cette aide"
                          style={{
                            padding: '5px 10px', borderRadius: 6, fontSize: 12,
                            background: '#D4724A', color: '#fff',
                            border: 'none',
                            textDecoration: 'none', fontWeight: 600,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Éditer
                        </Link>
                        <Link
                          to={`/admin/aides/catalogue/${aide.id}/history`}
                          title="Voir l'historique des modifications"
                          style={{
                            padding: '5px 10px', borderRadius: 6, fontSize: 12,
                            background: dark ? '#2d2d4e' : '#F0EDE8',
                            color: dark ? '#e0e0e0' : '#2D2D2D',
                            border: `1px solid ${border}`,
                            textDecoration: 'none', fontWeight: 500,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Historique
                        </Link>
                        <Link
                          to={`/aides/${aide.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Voir la fiche publique"
                          style={{
                            padding: '5px 10px', borderRadius: 6, fontSize: 12,
                            background: dark ? '#2d2d4e' : '#F0EDE8',
                            color: dark ? '#e0e0e0' : '#2D2D2D',
                            border: `1px solid ${border}`,
                            textDecoration: 'none', fontWeight: 500,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Voir
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </AdminCard>
      )}

      {/* Pagination */}
      {!loading && !error && aides.length > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: 20, flexWrap: 'wrap', gap: 12,
        }}>
          <span style={{ fontSize: 13, color: mutedColor }}>
            Page {cursorStack.length + 1} · {aides.length} aide{aides.length !== 1 ? 's' : ''} affichée{aides.length !== 1 ? 's' : ''}
          </span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handlePrev}
              disabled={isFirstPage}
              aria-label="Page précédente"
              style={{
                padding: '10px 20px', borderRadius: 10,
                border: `1px solid ${border}`,
                background: isFirstPage ? (dark ? '#1e1e3a' : '#F5F3F0') : (dark ? '#2d2d4e' : '#FDFCFA'),
                color: isFirstPage ? mutedColor : textColor,
                fontSize: 14, cursor: isFirstPage ? 'not-allowed' : 'pointer',
                fontWeight: 500, opacity: isFirstPage ? 0.5 : 1,
              }}
            >
              ← Page précédente
            </button>
            <button
              onClick={handleNext}
              disabled={!nextCursor}
              aria-label="Page suivante"
              style={{
                padding: '10px 20px', borderRadius: 10,
                border: 'none',
                background: nextCursor ? '#D4724A' : (dark ? '#1e1e3a' : '#F5F3F0'),
                color: nextCursor ? '#fff' : mutedColor,
                fontSize: 14, cursor: nextCursor ? 'pointer' : 'not-allowed',
                fontWeight: 600, opacity: nextCursor ? 1 : 0.5,
              }}
            >
              Page suivante →
            </button>
          </div>
        </div>
      )}

      {/* Lien retour */}
      <div style={{ marginTop: 32, paddingTop: 20, borderTop: `1px solid ${border}` }}>
        <Link
          to="/admin/dashboard"
          style={{ color: mutedColor, fontSize: 14, textDecoration: 'none' }}
        >
          ← Retour au tableau de bord
        </Link>
      </div>
    </div>
  );
}
